<template>
  <div class="exam">
    <div v-if="isMobile" class="exam_modal">
      <div class="exam__ilu-wrapper">
        <div class="exam__titlebar">
          <TitleBarMobile
            type="exam"
            @close-modal="closeModal()"
            @close-timer="showCurrentTime = false"
          />
        </div>
      </div>
    </div>
    <div
      v-if="currentCertification.id && !isLoading && !isMobile"
      class="exam__modal"
    >
      <div class="exam__ilu-wrapper">
        <div class="exam__wrapper">
          <div class="exam__titlebar">
            <TitleBar
              v-if="!hasStarted && !isLoadingModal"
              :title="contentTitle"
              :showRemainingTime="isModalShown"
              type="exam"
              @close-modal="closeModal()"
              @close-timer="showCurrentTime = false"
            />
          </div>
          <div v-if="!isLoadingModal" class="exam__content-wrapper">
            <div
              class="exam__content-left"
              v-if="currentCertification.logo_image_url"
            >
              <div class="exam__badge-logo">
                <img :src="currentCertification.logo_image_url" />
              </div>
            </div>
            <div class="exam__content-right">
              <div>
                <h3 class="exam__motivator">Let's Get Certified!</h3>
                <div class="exam__count-wrapper">
                  <p class="exam__count">
                    Questions: {{ questionCount }}, Time:
                    {{
                      $duration.durationFromSecs(
                        currentCertification.time_cap_in_secs,
                        'zeros'
                      )
                    }}
                  </p>
                </div>
                <div
                  class="exam__instructions flat-scroll"
                  v-if="
                    lastActiveVoucher && lastActiveVoucher.certification_version
                  "
                >
                  <div
                    class="exam__instructions__content"
                    v-html="
                      lastActiveVoucher.certification_version.instructions_html
                    "
                  ></div>
                </div>
                <div class="exam__terms">
                  <label class="answer-checkbox">
                    <span class="answer-checkbox__input">
                      <input
                        type="checkbox"
                        name="checkbox"
                        id="exam__terms-id"
                        v-model="termsAccepted"
                        :checked="termsAccepted"
                      />
                      <span class="answer-checkbox__control">
                        <CheckboxTick />
                      </span>
                    </span>
                    <div class="exam__terms__tos">
                      I have read the instructions and confirm that I am ready
                      to attempt the certification exam and I agree to the
                      <span
                        class="exam__terms__tos-link"
                        @click.prevent="tosOpen = true"
                        >terms and conditions.</span
                      >
                    </div>
                  </label>
                </div>
              </div>
              <div class="exam__btn-wrapper">
                <button
                  @click="closeModal()"
                  class="btn btn--secondary exam__exit__btn"
                >
                  Exit / Close
                </button>
                <button
                  @click="openModal()"
                  class="btn btn--primary exam__take-exam-btn"
                  :disabled="!termsAccepted"
                >
                  Start Exam
                </button>
              </div>
            </div>
          </div>
          <div v-else class="exam__loader-wrapper">
            <ActionLoader loaderClass="exam__loader" />
          </div>
        </div>
      </div>
    </div>
    <div class="exam__alert" v-if="tosOpen && !isMobile">
      <AlertModal @close-modal="tosOpen = false">
        <h1 class="exam__alert__title">Terms and conditions</h1>
        <p class="exam__alert__text">
          BY CHOOSING TO BEGIN THIS EXAM, YOU AGREE NOT TO REVEAL ANY OF THE
          CONTENT COVERED IN THIS EXAM. THIS INCLUDES, BUT IS NOT LIMITED TO,
          QUESTIONS, ANSWERS, AND ALL LAB CONTENT. IF YOU ARE FOUND IN VIOLATION
          OR FAIL TO COMPLY, IT MAY RESULT IN FORFEITURE OF ALL RELEVANT INE
          CERTIFICATIONS.
        </p>
        <p class="exam__alert__text">
          PLEASE NOTE THAT WE WILL PROVIDE YOU WITH ALL OF THE MATERIALS
          NECESSARY TO COMPLETE THIS EXAM WITHIN THE PLATFORM. YOU ARE NOT
          REQUIRED TO HAVE AN EXISTING AZURE, AWS, OR GOOGLE SUBSCRIPTION. INE
          IS NOT RESPONSIBLE FOR THE COSTS OF ANY RESOURCES CREATED OUTSIDE OF
          THE INE PLATFORM WHILE ATTEMPTING THIS EXAM.
        </p>
        <button
          @click="tosOpen = false"
          class="btn btn--primary exam__alert__cta"
        >
          Close
        </button>
      </AlertModal>
    </div>
    <div class="exam__modal-wrapper" v-if="!isMobile">
      <ExamModal
        v-if="isModalShown"
        v-bind="{
          certification: currentCertification,
          attempt: currentAttempt,
          title: contentTitle,
          context: contentType,
          voucherId: lastActiveVoucher.id,
        }"
        @close-modal="closeModal"
        @start-issue="startIssue"
        @exam-error="examError"
      />
    </div>
    <div class="exam__modal-mobile-wrapper" v-else>
      <h4 class="exam__modal-mobile-title">We’re sorry!</h4>
      <div class="exam__modal-mobile-content">
        Currently, INE Certification Exams can't be executed on mobile devices.
        Please try again on a computer.
      </div>
      <button
        class="btn btn--primary exam__modal-mobile-button"
        @click="closeModal()"
      >
        Go back
      </button>
    </div>
  </div>
</template>

<script>
import { differenceInSeconds } from 'date-fns'
import { mapState } from 'vuex'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import TitleBar from '@/components/modals/quiz-modal/TitleBar.vue'
import TitleBarMobile from '@/components/modals/quiz-modal/TitleBarMobile.vue'
import ExamModal from '@/components/modals/ExamModal.vue'
import AlertModal from '@/components/certifications/AlertModal.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'CertificationExam',
  components: {
    TitleBar,
    TitleBarMobile,
    ExamModal,
    ActionLoader,
    AlertModal,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
  },
  data() {
    return {
      isModalShown: false,
      isLoading: true,
      isLoadingModal: false,
      hasStarted: false,
      termsAccepted: false,
      tosOpen: false,
    }
  },
  computed: {
    ...mapState({
      currentCertification: state => state.certifications.currentCertification,
      previousAttempts: state => state.certifications.previousAttempts,
      currentAttempt: state => state.certifications.currentAttempt,
      learningPath: state => state.learningPaths.currentLearningPath,
    }),
    contentType() {
      return CONSTANTS.PARENT_CONTENT_TYPES_KEYS.CERTIFICATION
    },
    questionCount() {
      const { amount_questions: totalQuestions } = this.currentCertification
      return totalQuestions
    },
    hasAttemptInProgress() {
      return (
        this.isAttemptActive ||
        !!(this.currentAttempt && this.currentAttempt.id)
      )
    },
    isAttemptActive() {
      const { lastAttempt } = this
      return (
        lastAttempt &&
        lastAttempt.status ===
          CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS &&
        differenceInSeconds(
          new Date(lastAttempt.expiration_date).getTime(),
          new Date().getTime()
        ) > 0
      )
    },
    lastAttempt() {
      const { activeVouchers } = this
      if (activeVouchers?.length) {
        if (activeVouchers[0].active_attempts?.length) {
          return activeVouchers[0].active_attempts[0]
        }
        if (activeVouchers[0].inactive_attempts?.length) {
          return activeVouchers[0].inactive_attempts[0]
        }
      }
      return null
    },
    lastActiveVoucher() {
      const { voucherId } = this.$route.params
      if (this.activeVouchers?.length) {
        return this.activeVouchers[0]
      }
      if (voucherId) {
        return { id: voucherId }
      }
      return null
    },
    activeVouchers() {
      if (!this.currentCertification.user_status) return null
      const {
        currentCertification: {
          user_status: { active_vouchers },
        },
      } = this
      return active_vouchers
    },
    previousAttemptCount() {
      if (!Array.isArray(this.previousAttempts)) return 0
      return this.previousAttempts.filter(
        a => a.status === CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.FINISHED
      ).length
    },
    contentTitle() {
      return `${this.currentCertification.name} v${this.currentCertification.latest_version}`
    },
    eventContextData() {
      return {
        certification_id: this.currentCertification.id,
        certification_name: this.currentCertification.name,
        attempt_id: this.currentAttempt.id,
        created: this.currentAttempt.created,
        expiration_date: this.currentAttempt.expiration_date,
        learning_area_id: this.currentCertification.learning_area?.id,
        learning_area_name: this.currentCertification.learning_area?.name,
        latest_version: this.currentCertification.latest_version,
        is_beta: this.currentCertification.is_beta,
        certification_status: this.currentCertification.status,
        attempt_status: this.currentAttempt.status,
      }
    },
    isMobile() {
      return window.innerWidth <= 768
    },
  },
  methods: {
    async openModal() {
      this.hasStarted = true
      this.isLoadingModal = true
      this.$emit('is-loading', true)
      if (!this.hasAttemptInProgress) {
        await this.$store.dispatch('certifications/startExamAttempt', {
          voucherId: this.lastActiveVoucher.id,
        })
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_ATTEMPT_CREATED,
          {
            ...this.eventContextData,
          }
        )
        window.history.replaceState(
          {},
          null,
          `${this.$route.path}/${this.currentAttempt.id}`
        )
      }
      this.$emit('is-loading', false)

      // Update hand-on lab for exams or reset any old lab we could have
      this.$store.dispatch('labs/updateCurrentLab', {
        currentLab: {
          id: undefined,
          user_status: undefined,
          user_status_modified: undefined,
          global_running_session: undefined,
          available_resets: undefined,
        },
      })
      const lab = this.currentAttempt?.practice_lab
      if (lab) {
        await this.$store.dispatch('labs/getLabById', lab)
      }
      this.isLoadingModal = false
      this.isModalShown = true
    },
    closeModal() {
      this.exitExam()
      if (this.learningPath && this.learningPath.id) {
        this.$router.push(this.$buildContentLink('path', this.learningPath))
      } else {
        this.$router.push({ name: 'home' })
      }
      this.isModalShown = false
    },
    startIssue(payload) {
      this.$store.dispatch('content/saveContentIssue', {
        content_type: 'quiz',
        content_name: this.contentTitle,
        content_id: this.content.id,
        extra_args: {
          ...payload,
        },
      })
    },
    exitExam() {
      const body = document.querySelector('body')
      body.classList.remove('modal-open')
      this.$store.dispatch('certifications/clearExamStore')
    },
    examError() {
      this.closeModal()
    },
  },
  async created() {
    this.isLoading = true

    const { voucherId, attemptId } = this.$route.params
    if (attemptId) {
      await this.$store.dispatch('certifications/resumeExamAttempt', {
        voucherId,
        attemptId,
      })
      this.sendMixpanelEvent(
        this.eventsConstants.CERTIFICATION_ATTEMPT_RESUMED,
        {
          ...this.eventContextData,
        }
      )
      await this.$nextTick()
      this.openModal()
    }

    if (!this.currentCertification.id) {
      this.exitExam()
      this.$store.dispatch(
        'app/setError',
        `Please select a Certification from a Learning Path`
      )
      this.$router.push({
        name: 'home',
      })
    }

    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.isLoading = false
  },
  destroyed() {
    this.exitExam()
  },
}
</script>

<style lang="scss">
.exam {
  margin: 20px 0 0 0;
  color: var(--white);
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-color: var(--dark-gray);
    z-index: 203;
    overflow-y: auto;

    &-mobile {
      &-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
        min-height: 1141.61px;
        min-height: 1141.61px;
        justify-content: flex-start;
      }
      &-title {
        font-size: 20px;
        line-height: 26px;
        color: #ffffff;
      }
      &-content {
        font-size: 16px;
        line-height: 20px;
        max-width: 353px;
        align-self: center;
      }

      &-button {
        max-width: 85px;
        align-self: center;
        display: flex;
        justify-content: center;
        font-size: 14px;
        line-height: 20px;
        margin: 50px 0px;
      }
    }
  }
  &__ilu-wrapper {
    position: relative;
    overflow: hidden;
    height: 100%;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 204;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    overflow: auto;
  }
  &__content-wrapper {
    display: flex;
    position: relative;
    flex-wrap: wrap;
    max-width: 1200px;
    min-height: 100%;
    margin: 30px auto 0 auto;
    background: #252525 0% 0% no-repeat padding-box;
    box-shadow: 6px 0px 50px #0000001a;
    border-radius: 10px;
    overflow: hidden;
    align-self: flex-start;
    @include tablet {
      min-height: unset;
      padding: 50px 75px;
      margin: 30px auto 30px auto;
      flex-wrap: nowrap;
    }
    &:before {
      content: '';
      position: absolute;
      top: -30%;
      left: -24%;
      width: 667px;
      height: 711px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #215bc7 0%, #2113b100 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.28;
    }
  }
  &__titlebar {
    width: 100%;
    align-self: flex-start;
  }
  &__content-left {
    width: 100%;
    padding: 20px;
    text-align: center;
    margin: 0 0 -200px 0;
    display: none;
    @include tablet {
      margin: 0;
      width: 30%;
      display: block;
    }
  }
  &__badge-logo {
    position: relative;
    width: 80%;
    max-width: 300px;
    img {
      width: 100%;
      image-rendering: -moz-crisp-edges; /* Firefox */
      image-rendering: -o-crisp-edges; /* Opera */
      image-rendering: -webkit-optimize-contrast; /* Webkit (non-standard naming) */
      image-rendering: crisp-edges;
      -ms-interpolation-mode: nearest-neighbor; /* IE (non-standard property) */
    }
  }
  &__content-right {
    width: 100%;
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    @include tablet {
      padding: 0 50px 0 50px;
      width: 70%;
      margin: -30px 0 0 0;
      -ms-flex-wrap: nowrap;
      flex-wrap: wrap;
      -webkit-box-pack: start;
      -ms-flex-pack: start;
      justify-content: flex-start;
    }
  }
  &__motivator {
    text-transform: uppercase;
    font-family: var(--font-medium);
    letter-spacing: 0;
    white-space: nowrap;
    font-size: 2rem;
    margin-bottom: 5px;
    @include tablet {
      font-size: 40px;
    }
  }
  &__count-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }
  &__count {
    font-size: 1rem;
    font-weight: 700;
    @include tablet {
      font-size: 22px;
    }
  }
  &__count-label {
    font-size: 32px;
    font-weight: 700;
  }
  &__btn-wrapper {
    display: flex;
    width: 100%;
    margin: 50px 0 0 0;
    justify-content: center;
    align-content: center;
    flex-wrap: wrap;
    @include mobile {
      margin: -75px 0 0 0;
    }
    @include tablet {
      margin: 60px 0 0 0;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: flex-end;
      width: 100%;
      justify-content: flex-end;
    }
  }
  &__alert {
    &__title {
      text-align: center;
      font: normal normal bold 32px/46px var(--font-medium);
      letter-spacing: 0px;
      width: 500px;
      margin: 30px auto;
    }
    &__text {
      text-align: center;
      font: normal normal normal 18px/28px var(--font-book);
      letter-spacing: -0.44px;
      max-width: 800px;
      margin: 40px 0;
    }
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
  &__instructions {
    font-size: 16px;
    overflow: scroll;
    max-height: 300px;
    padding: 15px 30px;
    background: var(--dark-brighter) 0% 0% no-repeat padding-box;
    border-radius: 10px;
    a {
      color: #ffffff;
      text-decoration: underline;
    }
  }
  &__terms {
    margin-top: 30px;
    font-size: 18px;
    &__tos {
      display: inline;
      &-link {
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  &__exit__btn {
    margin-right: 20px;
  }
}
</style>
